import { Segmented, Table } from 'antd'
import { useMemo, useState, type Key } from 'react'
import { MdOutlineTaskAlt, MdStar } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'
import { useLayoutHeaderHeight } from '@/Ship/Hooks/useLayoutHeaderHeight'
import { pluralizeUk } from '@/Ship/Helpers/pluralize'

import { CompletionAssignments } from '../Components/CompletionAssignments'
import { GroupName } from '../Components/GroupName'
import { ReportExportButton } from '../Components/ReportExportButton'
import { ReportFilters } from '../Components/ReportFilters'
import { ReportShell } from '../Components/ReportShell'
import { ReportSummary, type ReportKpi } from '../Components/ReportSummary'
import { ReportEmpty, ReportError } from '../Components/ReportTableState'
import type { CompletionGroupBy, CompletionReport, CompletionRow, ReportFilterState } from '../Contracts/Report'
import { COMPLETION_GROUPS, GROUP_TITLES, groupKeyParam, groupLabel, isPeriodGroup } from '../Helpers/reportGroups'
import { taskReportShellChips } from '../Helpers/reportChips'
import { formatPeriodLabel } from '../Helpers/reportPeriod'
import { buildReportParams, initialReportFilters } from '../Helpers/reportQuery'
import { useReportFetch } from '../Hooks/useReportFetch'
import { useTableSort } from '../Hooks/useTableSort'

import styles from '../Components/Report.module.css'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

import '@/Ship/Templates/CrudResource/Table.css'

const formatNumber = (value: number, digits = 0): string =>
    value.toLocaleString('uk-UA', { maximumFractionDigits: digits })

const delayedShare = (delayed: number, tasks: number): number => (tasks > 0 ? Math.round((delayed / tasks) * 100) : 0)

/** Колонки таблиці з сортуванням (ключі — як `sort` в експорті) */
type CompletionSortKey = 'group' | 'tasks' | 'trips' | 'tonnes' | 'delayed' | 'averageRating'

const byNumber = (field: 'tasks' | 'trips' | 'tonnes' | 'delayed') => (a: CompletionRow, b: CompletionRow) =>
    a[field] - b[field]

/**
 * Звіт «Виконання задач»: підтверджені диспетчером задачі за період у розрізі водія, ТЗ,
 * перевізника, диспетчера або дня / тижня / місяця. Рядок розгортається в список задач.
 */
const CompletionPage = () => {
    const [filters, setFilters] = useState<ReportFilterState>(initialReportFilters)
    const [groupBy, setGroupBy] = useState<CompletionGroupBy>('driver')
    const [expandedKeys, setExpandedKeys] = useState<Key[]>([])
    // Сортування кероване: експорт віддає рядки в тому ж порядку, що й таблиця
    const { sortOrder, onChange: onSortChange, sortParams } = useTableSort<CompletionSortKey, CompletionRow>()
    const headerHeight = useLayoutHeaderHeight()

    const params = useMemo(() => buildReportParams(filters), [filters])
    const { data, dataParams, loading, error, reload } = useReportFetch<CompletionReport>(
        '/services/reports/completion',
        { ...params, groupBy }
    )

    // Інші фільтри чи розріз — інші групи: розгорнуті рядки згортаємо одразу (не в ефекті),
    // щоб деталізація не встигла запросити старий ключ з новим розрізом
    const changeFilters = (next: ReportFilterState) => {
        setExpandedKeys([])
        setFilters(next)
    }
    const changeGroupBy = (next: CompletionGroupBy) => {
        setExpandedKeys([])
        setGroupBy(next)
    }
    // Рядки попереднього розрізу не показуємо з новим (ключ «3» не можна форматувати як дату)
    const rows = data && dataParams?.['groupBy'] === groupBy ? data.items : []

    const totals = data?.totals
    const average = totals?.averageRating ?? null
    const kpis: ReportKpi[] = [
        { key: 'tasks', label: 'Виконано задач', value: formatNumber(totals?.tasks ?? 0) },
        { key: 'trips', label: 'Ходок', value: formatNumber(totals?.trips ?? 0) },
        { key: 'tonnes', label: 'Перевезено, т', value: formatNumber(totals?.tonnes ?? 0, 3) },
        {
            key: 'delayed',
            label: 'Із затримкою',
            value: formatNumber(totals?.delayed ?? 0),
            hint: `${delayedShare(totals?.delayed ?? 0, totals?.tasks ?? 0)}% задач`,
        },
        {
            key: 'rating',
            label: 'Середня оцінка водіїв',
            value:
                average !== null ? (
                    <>
                        <MdStar className={cx(styles, 'star')} aria-hidden="true" />
                        {average.toFixed(1)}
                    </>
                ) : (
                    '—'
                ),
            hint: `${totals?.ratingsCount ?? 0} ${pluralizeUk(totals?.ratingsCount ?? 0, ['оцінка', 'оцінки', 'оцінок'])}`,
        },
    ]

    const columns = [
        {
            title: GROUP_TITLES[groupBy],
            key: 'group',
            sortOrder: sortOrder('group'),
            sorter: (a: CompletionRow, b: CompletionRow) =>
                isPeriodGroup(groupBy)
                    ? (a.key ?? '').localeCompare(b.key ?? '')
                    : (groupLabel(groupBy, a) ?? '').localeCompare(groupLabel(groupBy, b) ?? '', 'uk'),
            render: (_: unknown, row: CompletionRow) => <GroupName groupBy={groupBy} row={row} />,
        },
        {
            title: 'Задач',
            dataIndex: 'tasks',
            key: 'tasks',
            width: 100,
            align: 'right' as const,
            sortOrder: sortOrder('tasks'),
            sorter: byNumber('tasks'),
            render: (v: number) => <span className={cx(styles, 'num')}>{formatNumber(v)}</span>,
        },
        {
            title: 'Ходок',
            dataIndex: 'trips',
            key: 'trips',
            width: 100,
            align: 'right' as const,
            sortOrder: sortOrder('trips'),
            sorter: byNumber('trips'),
            render: (v: number) => <span className={cx(styles, 'num')}>{formatNumber(v)}</span>,
        },
        {
            title: 'Тонн',
            dataIndex: 'tonnes',
            key: 'tonnes',
            width: 120,
            align: 'right' as const,
            sortOrder: sortOrder('tonnes'),
            sorter: byNumber('tonnes'),
            render: (v: number) => <span className={cx(styles, 'num')}>{formatNumber(v, 3)}</span>,
        },
        {
            title: 'Із затримкою',
            dataIndex: 'delayed',
            key: 'delayed',
            width: 140,
            align: 'right' as const,
            sortOrder: sortOrder('delayed'),
            sorter: byNumber('delayed'),
            render: (v: number, row: CompletionRow) =>
                v > 0 ? (
                    <span className={cx(styles, 'num')}>
                        <span className={cx(styles, 'delayed')}>{formatNumber(v)}</span>
                        <span className={cx(styles, 'faint')}> · {delayedShare(v, row.tasks)}%</span>
                    </span>
                ) : (
                    <span className={cx(styles, 'faint')}>0</span>
                ),
        },
        {
            title: 'Сер. оцінка',
            dataIndex: 'averageRating',
            key: 'averageRating',
            width: 130,
            sortOrder: sortOrder('averageRating'),
            sorter: (a: CompletionRow, b: CompletionRow) => (a.averageRating ?? -1) - (b.averageRating ?? -1),
            render: (v: number | null, row: CompletionRow) =>
                v === null ? (
                    <span className={cx(styles, 'faint')}>—</span>
                ) : (
                    <span
                        className={cx(styles, 'rating')}
                        title={`${row.ratingsCount} ${pluralizeUk(row.ratingsCount, ['оцінка', 'оцінки', 'оцінок'])}`}
                    >
                        <MdStar style={{ color: '#faad14' }} aria-hidden="true" />
                        {v.toFixed(1)}
                        <span className={cx(styles, 'faint')}>({row.ratingsCount})</span>
                    </span>
                ),
        },
    ]

    const hasFilters = Object.keys(params).some((k) => !['from', 'to'].includes(k))
    const subtitle = totals
        ? `${formatPeriodLabel(filters.period)} · ${formatNumber(totals.tasks)} ${pluralizeUk(totals.tasks, ['задача', 'задачі', 'задач'])}`
        : formatPeriodLabel(filters.period)

    return (
        <ReportShell
            title="Виконання задач"
            subtitle={subtitle}
            filterPanel={<ReportFilters value={filters} onChange={changeFilters} />}
            chips={taskReportShellChips(filters, changeFilters)}
            onClearFilters={() => changeFilters(initialReportFilters())}
            onRefresh={reload}
            refreshing={loading}
            storageKey="autostrada.report.completion.filterOpen"
            actions={
                <ReportExportButton
                    url="/services/reports/completion/export"
                    params={{ ...params, groupBy, ...sortParams }}
                    fallbackFileName="Виконання задач.xlsx"
                />
            }
        >
            <ReportSummary items={kpis} loading={loading && !data} />

            <div className={cx(styles, 'toolbar')}>
                <div className={cx(styles, 'toolbarGroup')}>
                    <span className={cx(styles, 'toolbarLabel')}>Групувати за</span>
                    <div className={cx(styles, 'scrollX')}>
                        <Segmented<CompletionGroupBy>
                            options={COMPLETION_GROUPS}
                            value={groupBy}
                            onChange={changeGroupBy}
                        />
                    </div>
                </div>
                <span className={cx(styles, 'toolbarHint')}>Клік по рядку розгортає задачі групи</span>
            </div>

            {error ? (
                <ReportError message={error} onRetry={reload} />
            ) : (
                <Table<CompletionRow>
                    className={`wl-table ${cx(styles, 'table')}`}
                    size="middle"
                    rowKey={groupKeyParam}
                    loading={loading}
                    columns={columns}
                    dataSource={rows}
                    onChange={onSortChange}
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: false,
                        showTotal: (total, range) => (total > 0 ? `${range[0]}–${range[1]} з ${total}` : ''),
                    }}
                    scroll={{ x: 'max-content' }}
                    sticky={{ offsetHeader: headerHeight }}
                    locale={{
                        emptyText: loading ? (
                            ' '
                        ) : (
                            <ReportEmpty hasFilters={hasFilters} allTime={filters.period === null} />
                        ),
                    }}
                    rowClassName={() => 'wl-table-row-clickable'}
                    expandable={{
                        expandedRowKeys: expandedKeys,
                        onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
                        expandRowByClick: true,
                        expandedRowRender: (row) => (
                            <CompletionAssignments groupBy={groupBy} groupKey={groupKeyParam(row)} params={params} />
                        ),
                    }}
                />
            )}
        </ReportShell>
    )
}

export default CompletionPage

export const pageConfig: IPageConfig = {
    route: '/reports/completion',
    key: 'Report.Completion',
    title: 'Виконання задач',
    icon: <MdOutlineTaskAlt />,
}
